/**
 * 新建面试 —— 一个表单搞定。
 *
 * 极简:岗位 + 候选人 + 时间 + 创建。
 * 没有岗位/候选人时,可以现场展开 inline 表单建。
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createCandidate, createInterview, createJob,
  listCandidates, listJobs, uploadResume,
} from '../api/interview';
import type { Candidate, Job } from '../api/types';

export default function AdminNew() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 主表单
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedCand, setSelectedCand] = useState('');
  const [scheduledAt, setScheduledAt] = useState(() => {
    // datetime-local 需要本地时间字符串(不带时区),
    // toISOString() 是 UTC,中国 +8 区会差 8 小时,这是常见 bug。
    const d = new Date(); d.setMinutes(d.getMinutes() + 5);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
           `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [duration, setDuration] = useState(30);

  // inline 新建岗位
  const [showNewJob, setShowNewJob] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [jobDesc, setJobDesc] = useState('');

  // inline 新建候选人
  const [showNewCand, setShowNewCand] = useState(false);
  const [candName, setCandName] = useState('');
  const [candEmail, setCandEmail] = useState('');
  const [candPhone, setCandPhone] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);

  const refresh = async () => {
    const [jb, cd] = await Promise.all([listJobs(), listCandidates()]);
    setJobs(jb); setCandidates(cd);
  };
  useEffect(() => { void refresh(); }, []);

  // 新建岗位
  const handleNewJob = async () => {
    if (!jobTitle.trim() || !jobDesc.trim()) {
      setError('请填岗位名称和描述'); return;
    }
    try {
      setBusy(true);
      const job = await createJob({
        title: jobTitle, description: jobDesc,
      });
      await refresh();
      setSelectedJob(job.id);
      setShowNewJob(false);
      setJobTitle(''); setJobDesc('');
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  // 新建候选人
  const handleNewCand = async () => {
    if (!candName.trim() && !resumeFile) {
      setError('请填姓名或上传简历'); return;
    }
    try {
      setBusy(true);
      let cand: Candidate;
      if (resumeFile) {
        cand = await uploadResume(resumeFile, { name: candName, email: candEmail });
      } else {
        cand = await createCandidate({ name: candName, email: candEmail, phone: candPhone });
      }
      await refresh();
      setSelectedCand(cand.id);
      setShowNewCand(false);
      setCandName(''); setCandEmail(''); setCandPhone(''); setResumeFile(null);
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  const handleSubmit = async () => {
    if (!selectedJob || !selectedCand) {
      setError('请选岗位和候选人'); return;
    }
    try {
      setBusy(true);
      const iv = await createInterview({
        candidate_id: selectedCand,
        job_id: selectedJob,
        scheduled_at: new Date(scheduledAt).toISOString(),
        duration_minutes: duration,
      });
      nav(`/admin/interview/${iv.id}`);
    } catch (e: any) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="container narrow">
      <div className="page-header">
        <h1 className="title">新建面试</h1>
      </div>

      {error && <div className="banner error">{error}</div>}

      <div className="card">
        <div className="col">
          {/* 岗位 */}
          <label>
            <div className="hint" style={{ marginBottom: 4 }}>岗位</div>
            <div className="row">
              <select value={selectedJob} onChange={(e) => setSelectedJob(e.target.value)} style={{ flex: 1 }}>
                <option value="">-- 选择 --</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>{j.title}</option>
                ))}
              </select>
              <button className="ghost" onClick={() => setShowNewJob(!showNewJob)}>
                {showNewJob ? '取消' : '+ 新建'}
              </button>
            </div>
          </label>

          {showNewJob && (
            <div className="card flat" style={{ margin: 0, marginTop: -4 }}>
              <div className="col" style={{ gap: 10 }}>
                <input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="岗位名称(如:高级 Python 后端)" />
                <textarea rows={3} value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} placeholder="JD / 要求" />
                <div className="hint">
                  这里只创建简单岗位 · 想要 AI 自动生成能力清单,请用「+ 新建岗位」走 wizard
                </div>
                <button onClick={handleNewJob} disabled={busy}>{busy ? '...' : '保存岗位'}</button>
              </div>
            </div>
          )}

          {/* 候选人 */}
          <label style={{ marginTop: 12 }}>
            <div className="hint" style={{ marginBottom: 4 }}>候选人</div>
            <div className="row">
              <select value={selectedCand} onChange={(e) => setSelectedCand(e.target.value)} style={{ flex: 1 }}>
                <option value="">-- 选择 --</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || c.email || c.id.slice(0, 8)}</option>
                ))}
              </select>
              <button className="ghost" onClick={() => setShowNewCand(!showNewCand)}>
                {showNewCand ? '取消' : '+ 新建'}
              </button>
            </div>
          </label>

          {showNewCand && (
            <div className="card flat" style={{ margin: 0, marginTop: -4 }}>
              <div className="col" style={{ gap: 8 }}>
                <input value={candName} onChange={(e) => setCandName(e.target.value)} placeholder="姓名" />
                <input value={candEmail} onChange={(e) => setCandEmail(e.target.value)} placeholder="邮箱(选填)" />
                <input value={candPhone} onChange={(e) => setCandPhone(e.target.value)} placeholder="电话(选填)" />
                <input type="file" accept=".pdf,.docx,.png,.jpg" onChange={(e) => setResumeFile(e.target.files?.[0] || null)} />
                {resumeFile && <span className="hint">📎 {resumeFile.name}</span>}
                <button onClick={handleNewCand} disabled={busy}>{busy ? '...' : '保存候选人'}</button>
              </div>
            </div>
          )}

          {/* 时间 */}
          <label style={{ marginTop: 12 }}>
            <div className="hint" style={{ marginBottom: 4 }}>开始时间</div>
            <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
          </label>

          {/* 时长 */}
          <label>
            <div className="hint" style={{ marginBottom: 4 }}>时长</div>
            <div className="row">
              {[15, 30, 45, 60].map((m) => (
                <span key={m} className={`chip ${duration === m ? 'active' : ''}`} onClick={() => setDuration(m)}>
                  {m} 分钟
                </span>
              ))}
            </div>
          </label>

          <button onClick={handleSubmit} disabled={busy || !selectedJob || !selectedCand} style={{ marginTop: 16 }}>
            {busy ? '创建中...' : '创建面试'}
          </button>
        </div>
      </div>
    </div>
  );
}
